import React from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { H2 } from '@/common/styles/page';

interface ChatHeaderProps {
  showThreadList: boolean;
  showInfoPanel: boolean;
  onBackClick: () => void;
  onThreadListClick: () => void;
  onInfoClick: () => void;
  onNewConversation: () => void;
  onExport?: () => void;
  onClose: () => void;
  hasConversations: boolean;
  aiConnectionStatus: 'connected' | 'disconnected' | 'processing' | 'idle';
  showConnectionStatus: boolean;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  showThreadList,
  showInfoPanel,
  onBackClick,
  onThreadListClick,
  onInfoClick,
  onNewConversation,
  onExport,
  onClose,
  hasConversations,
  aiConnectionStatus,
  showConnectionStatus,
}) => (
  <>
    <HeaderContainer>
      <HeaderTitle>
        <ChatTitle>{showThreadList ? 'Conversation History' : 'Culture Copilot'}</ChatTitle>
      </HeaderTitle>

      <HeaderActions>
        <CloseButton onClick={onClose}>
          <Icon name="close" />
        </CloseButton>
      </HeaderActions>
    </HeaderContainer>

    <MenuBar>
      <LeftMenuActions>
        {showThreadList ? (
          <MenuButton onClick={onBackClick} title="Return to thread">
            <Icon name="reply" />
          </MenuButton>
        ) : (
          hasConversations && (
            <MenuButton onClick={onThreadListClick} title="Switch thread">
              <Icon name="list" />
            </MenuButton>
          )
        )}
        <MenuButton onClick={onNewConversation} title="New conversation">
          <Icon name="plus" />
        </MenuButton>
        {onExport && (
          <MenuButton onClick={onExport} title="Export thread">
            <Icon name="download" />
          </MenuButton>
        )}
      </LeftMenuActions>

      <RightMenuActions>
        <MenuButton onClick={onInfoClick} title="Page information" active={showInfoPanel}>
          <Icon name="info circle" />
        </MenuButton>
      </RightMenuActions>
    </MenuBar>
  </>
);

export default ChatHeader;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid rgba(165, 207, 238, 0.3);
  background-color: rgba(165, 207, 238, 0.12);
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  color: #2a3039;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;


const MenuBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(165, 207, 238, 0.3);
  background-color: #f8faff;
`;

const LeftMenuActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightMenuActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuButton = styled.button<{ active?: boolean }>`
  background: ${props => (props.active ? '#dbeafe' : 'none')};
  border: 1px solid #dbeafe;
  border-radius: 6px;
  cursor: pointer;
  width: 36px;
  height: 36px;
  color: #2a3039;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 0;
  line-height: 1;

  & > i,
  & > svg {
    display: block;
    margin: 0;
  }

  &:hover {
    background-color: #e0efff;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  color: #666d79;
  display: flex;
  align-items: center;
  &:hover {
    color: #2d70e2;
  }
`;

const ConnectionStatusBar = styled.div`
  display: flex;
  gap: 16px;
  padding: 6px 16px;
  background-color: #f8faff;
  border-bottom: 1px solid #e0e0e0;
  font-size: 11px;
`;

const ConnectionStatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusIndicator = styled.div<{ status: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props => {
    if (props.status === 'connected' || props.status === 'idle') return '#16a34a';
    if (props.status === 'processing') return '#f59e0b';
    return '#dc2626';
  }};
  ${props =>
    props.status === 'processing' &&
    `
    animation: pulse 1.5s ease-in-out infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
`;

const StatusText = styled.span`
  font-family: Raleway;
  font-size: 11px;
  color: #666d79;
`;

const ChatTitle = styled(H2)`
  margin: 0;
  display: flex;
  align-items: center;
  line-height: 1;
`;


